import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./CategoryNav.css";
import Splash from "../../common/Splash";
import LoginModal from "../LoginModal/LoginModal";

const MAX_GAMES_PER_SECTION = 18;

const SECTION_CONFIG = [
  {
    id: "HOT",
    title: "Hot",
    icon: "../../images/hot_game.png",
    apiType: "HOT",
    useFeatured: true,
  },
  {
    id: "SPORTS",
    title: "Sports",
    icon: "../../images/4.png",
    apiType: "SPORTS",
    staticSection: true,
  },
  {
    id: "LIVE",
    title: "Casino",
    icon: "../../images/5.png",
    apiType: "LIVE",
    system:
      "Astar,DreamGaming,EVOLUTION,EZUGI,Galaxsys,InOut,JILI,KoolBet,Microgaming,Mini,Pgs Gaming,Playtech,PP,SEXYBCRT,SmartSoft,V8,YeeBet",
  },
  {
    id: "SLOT",
    title: "Slot",
    icon: "../../images/6.png",
    apiType: "SLOT",
  },
  {
    id: "CRASH",
    title: "Crash",
    icon: "../../images/2.png",
    apiType: "EGAME",
    system:
      "100HP,2J,Aog,ATM,Bgaming,Casini,CQ9,EpicWin,EvoPlay,EZUGI,Galaxsys,InOut,JDB,JILI,KA,KoolBet,KY,Microgaming,Mini,Pgs Gaming,PIX,PP,SPRIBE,T1,TurboGames World,V8",
  },
  {
    id: "TABLE",
    title: "Table",
    icon: "../../images/17.png",
    apiType: "TABLE",
  },
  {
    id: "FISHING",
    title: "Fishing",
    icon: "../../images/fishing.png",
    apiType: "FH",
    system:
      "2J,Askmeslot,BtGaming,CQ9,FachaiGaming,FASTSPIN,JDB,JILI,KA,MT,NextSpin,VPlus,YGR Gaming",
  },
  {
    id: "ARCADE",
    title: "Arcade",
    icon: "../../images/7.png",
    apiType: "EGAME",
  },
  {
    id: "LOTTERY",
    title: "Lottery",
    icon: "../../images/3.png",
    apiType: "LOTTERY",
  },
];

const SPORTS_OPTIONS = [
  // { name: "9wickets", icon: "9wickets.png", link: "/exchange" },
  // { name: "Velki", icon: "velki.png", link: "/velki" },
  // {
  //   name: "SABA",
  //   icon: "saba.png",
  //   code: "08ced9dd788aed11ff3c7f387ae0f063",
  //   type: "Sports Game",
  //   platform: "SABASports(IBC)",
  // },
  { name: "9wickets", icon: "9wickets.png", link: "/exchange" },
  { name: "Velki", icon: "velki.png", link: "/exchange" },
  {
    name: "SABA",
    icon: "saba.png",
    code: "08ced9dd788aed11ff3c7f387ae0f063",
    type: "Sports Game",
    platform: "SABASports(IBC)",
  },
  { name: "United Gaming", icon: "ug.jpg", code: "c4b2813f6bbc5abf502ddfb857e604eb", type: "Sports Game", platform: 'UnitedGaming' },
  { name: "LuckSports", icon: "luckysports.svg", code: "92b24e4c25107367a80e0fe1a97c24e4", type: "Sports Game", platform: 'LuckySport' },
  { name: "TF", icon: "tf.png", code: "4ee8e0051a035b463b47c3c473ce317d", type: "Esports", platform: 'TF' },
  { name: "Bti", icon: "bti.png", code: "4d31f1186a81e208c003a7e37411ce35", type: "Sports Game", platform: 'bti' },
  { name: "DPE", icon: "dp.png", code: "e130116fdc9bcde2dbb31735b6c365d6", type: "Esports", platform: 'DPEsports' },
  { name: "DP", icon: "dp.png", code: "23c2dca76f87d7b7f239833060c8751e", type: "Sports Game", platform: 'DPSports' },
  { name: "SBO(VS)", icon: "sbobet.png", code: "341827d4370bb198b18364e2d75e6916", type: "VirtualSports ", platform: 'SBO' },
  { name: "SBO", icon: "sbo.png", code: "07baf9e1388d32cd4cee0c0c91b23020", type: "Sports Game", platform: 'SBO' },
  { name: "CMD", icon: "cmd.png", code: "1f7fbf84bf1bcc08c3a7ea27db75f366", type: "Sports", platform: 'CMD' },
  { name: "IA", icon: "ia.png", code: "68ddcab094a2375b08ca63eb33ef8673", type: "ESports", platform: 'IA' },

];

const makeInitialSections = () =>
  Object.fromEntries(
    SECTION_CONFIG.map((section) => [
      section.id,
      {
        games: [],
        loading: false,
        loaded: false,
      },
    ])
  );

const getGameTitle = (game) =>
  game?.name ||
  game?.title ||
  game?.game_name ||
  game?.platform ||
  game?.provider ||
  "Game";

const getGameCode = (game) =>
  game?.uid ||
  game?.game_code ||
  game?.code ||
  game?.gameCode ||
  game?.link ||
  "";

const getGamePlatform = (game) =>
  game?.platform || game?.provider || game?.system || "";

const getGameType = (game, fallbackType = "") =>
  game?.game_type || game?.type || fallbackType || "";

const getCardImage = (item, apiBase) => {
  if (item?.thubmnail) return `${apiBase}/storage/${item.thubmnail}`;
  if (item?.thumbnail && /^https?:\/\//i.test(item.thumbnail)) return item.thumbnail;
  if (item?.thumbnail) return `${apiBase}/storage/${item.thumbnail}`;
  if (item?.image) return item.image;
  if (item?.img) return item.img;
  if (item?.platform) return `${apiBase}/provider/${item.platform}.png`;
  return `https://placehold.co/240x240/101233/ffffff?text=${encodeURIComponent(
    getGameTitle(item)
  )}`;
};

export default function Categories({ marketBlock, featuredGames = [] }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [blockedSection, setBlockedSection] = useState("");
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  const [activeIndex, setActiveIndex] = useState(0);
  const [flippingIndex, setFlippingIndex] = useState(null);
  const [unflippingIndex, setUnflippingIndex] = useState(null);
  const [previousIndex, setPreviousIndex] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState("left");
  const [isContentReady, setIsContentReady] = useState(true);

  const [error, setError] = useState(null);

  const [sectionStates, setSectionStates] = useState(makeInitialSections);

  const [canCatScrollLeft, setCanCatScrollLeft] = useState(false);
  const [canCatScrollRight, setCanCatScrollRight] = useState(false);

  const categoryRefs = useRef([]);
  const tabRefs = useRef([]);
  const tabsWrapperRef = useRef(null);
  const categoriesWrapperRef = useRef(null);
  const requestIdRef = useRef(0);

  const swipeTouchStartX = useRef(null);
  const swipeTouchStartY = useRef(null);
  const swipeIsDragging = useRef(false);

  const origin = window.location.origin;
  const API = process.env.REACT_APP_API_URL;

  const isMobile =
    typeof window !== "undefined"
      ? window.innerWidth <= 768 || /Mobi|Android/i.test(window.navigator.userAgent)
      : false;

  const isLoggedIn = !!localStorage.getItem("token");

  const sections = SECTION_CONFIG;

  const activeSection = useMemo(() => sections[activeIndex], [sections, activeIndex]);

  const patchSection = useCallback((sectionId, patch) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        ...patch,
      },
    }));
  }, []);

  const updateCategoryScrollButtons = () => {
    const el = categoriesWrapperRef.current;
    if (!el) return;

    setCanCatScrollLeft(el.scrollLeft > 5);
    setCanCatScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };

  const scrollCategoriesBy = (dir) => {
    const el = categoriesWrapperRef.current;
    if (!el) return;

    const amount = Math.max(180, Math.floor(el.clientWidth * 0.72));
    el.scrollTo({
      left: el.scrollLeft + dir * amount,
      behavior: "smooth",
    });

    setTimeout(updateCategoryScrollButtons, 250);
  };

  const scrollToActiveCategory = (index) => {
    if (categoriesWrapperRef.current && categoryRefs.current[index]) {
      const container = categoriesWrapperRef.current;
      const activeElement = categoryRefs.current[index];

      const scrollLeft =
        activeElement.offsetLeft -
        container.offsetWidth / 2 +
        activeElement.offsetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: "smooth",
      });

      setTimeout(updateCategoryScrollButtons, 250);
    }
  };

  const handleSectionClick = (index) => {
    if (activeIndex !== index) {
      scrollToActiveCategory(index);

      setUnflippingIndex(activeIndex);
      setPreviousIndex(activeIndex);
      setAnimationDirection(index > activeIndex ? "right" : "left");

      setIsContentReady(false);
      setIsAnimating(true);
      setFlippingIndex(index);

      setTimeout(() => {
        setActiveIndex(index);
        setFlippingIndex(null);
        setUnflippingIndex(null);
      }, 175);

      setTimeout(() => {
        setPreviousIndex(null);
      }, 350);

      setTimeout(() => {
        setIsContentReady(true);
      }, 200);

      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const handleSwipeTouchStart = (e) => {
    swipeTouchStartX.current = e.touches[0].clientX;
    swipeTouchStartY.current = e.touches[0].clientY;
    swipeIsDragging.current = false;
  };

  const handleSwipeTouchMove = (e) => {
    if (swipeTouchStartX.current === null) return;
    const dx = Math.abs(e.touches[0].clientX - swipeTouchStartX.current);
    const dy = Math.abs(e.touches[0].clientY - swipeTouchStartY.current);
    if (dx > dy && dx > 10) swipeIsDragging.current = true;
  };

  const handleSwipeTouchEnd = (e) => {
    if (!swipeIsDragging.current || swipeTouchStartX.current === null) return;

    const dx = e.changedTouches[0].clientX - swipeTouchStartX.current;

    if (Math.abs(dx) > 50) {
      if (dx < 0 && activeIndex < sections.length - 1) {
        handleSectionClick(activeIndex + 1);
      } else if (dx > 0 && activeIndex > 0) {
        handleSectionClick(activeIndex - 1);
      }
    }

    swipeTouchStartX.current = null;
    swipeIsDragging.current = false;
  };

  const handleGameClick = async (gameCode, gamePlatform, gameType) => {
    if (marketBlock?.exchange_market === 0 && marketBlock?.status !== "active") {
      setBlockedSection("Sports");
      setShowBlockedModal(true);
      return;
    }

    if (!isLoggedIn) {
      setShowModal(true)
      return;
    }

    const token = localStorage.getItem("token");
    const remote_id = localStorage.getItem("remote_id");

    if (!token || !remote_id) {
      setError("User not logged in");
      return;
    }

    setIsLoading(true);

    try {
      if (typeof gameCode === "string" && /^https?:\/\//i.test(gameCode)) {
        navigate("/gamerun", { state: { iframeUrl: gameCode } });
        return;
      }

      if ((!gamePlatform || !gameType) && typeof gameCode === "string" && gameCode.includes("-")) {
        const parts = gameCode.split("-");
        if (parts.length >= 2) {
          gamePlatform = parts[0];
          gameType = parts[1];
        }
      }

      if (gamePlatform === "Lucky" || gamePlatform === "LuckySpodddrt") {
        const response = await fetch(`${API}/api/player-login`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: remote_id }),
        });

        const dataly = await response.json();

        if (dataly?.access_token) {
          navigate("/gamerun", {
            state: {
              iframeUrl: `https://sprodm.uni247.xyz/?access_token=${dataly.access_token}`,
            },
          });
          return;
        }
      }

      const response = await fetch(`${API}/api/inr-game-run`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: remote_id,
          externalURL: origin,
          isMobileLogin: isMobile,
          gameCode,
          gameType,
          platform: gamePlatform,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const iframeUrl = data?.url || data?.data?.payload?.game_launch_url;
        if (iframeUrl) {
          navigate("/gamerun", { state: { iframeUrl } });
        } else {
          setError(data?.message || "An unexpected error occurred");
        }
      } else {
        setError(data?.message || "An unexpected error occurred");
      }
    } catch (err) {
      console.error("Error launching game:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSectionGames = useCallback(
    async (section) => {
      if (!section || section.staticSection) return;

      if (section.id === "HOT" && Array.isArray(featuredGames) && featuredGames.length > 0) {
        patchSection("HOT", {
          games: featuredGames.slice(0, MAX_GAMES_PER_SECTION),
          loading: false,
          loaded: true,
        });
        return;
      }

      patchSection(section.id, { loading: true });

      const currentReqId = ++requestIdRef.current;

      try {
        let url = `${API}/api/get-games?type=${encodeURIComponent(section.apiType)}`;

        if (section.system) {
          url += `&system=${encodeURIComponent(section.system)}`;
        }

        const res = await fetch(url);
        const json = await res.json();

        if (currentReqId !== requestIdRef.current) return;

        const games = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.games)
            ? json.games
            : [];

        patchSection(section.id, {
          games: games.slice(0, MAX_GAMES_PER_SECTION),
          loading: false,
          loaded: true,
        });
      } catch (err) {
        console.error(`Failed to load ${section.id} games`, err);

        if (currentReqId !== requestIdRef.current) return;

        patchSection(section.id, {
          games: [],
          loading: false,
          loaded: true,
        });
      }
    },
    [API, featuredGames, patchSection]
  );

  const activeSectionState = sectionStates[activeSection?.id] || {
    games: [],
    loading: false,
    loaded: false,
  };

  useEffect(() => {
    if (!activeSection) return;

    if (activeSection.staticSection) {
      setIsContentReady(true);
      return;
    }

    // already loading? do not call again
    if (activeSectionState.loading) return;

    // already loaded? no need to fetch again
    if (activeSectionState.loaded) {
      setIsContentReady(true);
      return;
    }

    setIsContentReady(false);

    fetchSectionGames(activeSection).finally(() => {
      setIsContentReady(true);
    });
  }, [
    activeSection?.id,
    activeSection?.staticSection,
    activeSectionState.loading,
    activeSectionState.loaded,
    fetchSectionGames,
  ]);
  useEffect(() => {
    if (Array.isArray(featuredGames) && featuredGames.length > 0) {
      patchSection("HOT", {
        games: featuredGames.slice(0, MAX_GAMES_PER_SECTION),
        loading: false,
        loaded: true,
      });
    }
  }, [featuredGames, patchSection]);

  useEffect(() => {
    updateCategoryScrollButtons();

    const el = categoriesWrapperRef.current;
    if (!el) return;

    const handleScroll = () => updateCategoryScrollButtons();
    const handleResize = () => updateCategoryScrollButtons();

    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (isLoading) return <Splash />;

  return (
    <>
      <div className="category-rail-shell">
        <button
          type="button"
          className={`category-rail-arrow left ${!canCatScrollLeft ? "disabled" : ""}`}
          onClick={() => scrollCategoriesBy(-1)}
          disabled={!canCatScrollLeft}
          aria-label="Scroll categories left"
        >
          <ChevronLeft size={20} strokeWidth={2.6} />
        </button>

        <div
          ref={categoriesWrapperRef}
          className="category-rail"
          onScroll={updateCategoryScrollButtons}
        >
          {sections.map((item, index) => {
            const isActive = activeIndex === index;
            const isFlipping = flippingIndex === index;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSectionClick(index)}
                ref={(el) => (categoryRefs.current[index] = el)}
                className={`category-cardss ${isActive ? "active" : ""}`}
              >
                <div className="icon">
                  <div className={`catt-icon-wrapper${isFlipping ? " catt-spin" : ""}`}>
                    <img src={item.icon} alt={item.title} className="category-icon-img" />
                  </div>
                </div>

                <span className={`category-title ${isActive ? "actives" : ""}`}>
                  {item.title}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className={`category-rail-arrow right ${!canCatScrollRight ? "disabled" : ""}`}
          onClick={() => scrollCategoriesBy(1)}
          disabled={!canCatScrollRight}
          aria-label="Scroll categories right"
        >
          <ChevronRight size={20} strokeWidth={2.6} />
        </button>
      </div>

      <div
        className="tabs-wrapper"
        ref={tabsWrapperRef}
        onTouchStart={handleSwipeTouchStart}
        onTouchMove={handleSwipeTouchMove}
        onTouchEnd={handleSwipeTouchEnd}
        style={{ overflow: "hidden" }}
      >
        <div
          style={{
            display: "flex",
            width: `${sections.length * 100}%`,
            transform: `translateX(-${(activeIndex * 100) / sections.length}%)`,
            transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            alignItems: "flex-start",
          }}
        >
          {sections.map((section, sIndex) => {
            const state = sectionStates[section.id];
            const currentGames = state?.games || [];

            return (
              <div
                key={section.id}
                style={{
                  width: `${100 / sections.length}%`,
                  flexShrink: 0,
                  overflow: "hidden",
                  height: sIndex === activeIndex ? "auto" : 0,
                }}
              >
                <div className="slide-inner">
                  <div className="tabs-container">
                    {/* SPORTS STATIC */}
                    {section.id === "SPORTS" &&
                      SPORTS_OPTIONS.map((sport, index) => (
                        <div
                          key={sport.name}
                          ref={(el) => (tabRefs.current[index] = el)}
                          onClick={() => {
                            if (sport.link) navigate(sport.link);
                            else handleGameClick(sport.code, sport.platform, sport.type);
                          }}
                          className="tab-item"
                        >
                          <span className="tab-icon">
                            <img
                              src={`${API}/icon/${sport.icon}`}
                              alt={sport.name}
                              onError={(e) => {
                                e.currentTarget.src = `https://placehold.co/200x200/101233/ffffff?text=${encodeURIComponent(
                                  sport.name
                                )}`;
                              }}
                            />
                          </span>
                          <span className="tab-name">{sport.name}</span>
                        </div>
                      ))}

                    {/* API GAMES */}
                    {section.id !== "SPORTS" &&
                      state?.loading &&
                      sIndex === activeIndex && (
                        <div className="hg-state hg-state--big">Loading...</div>
                      )}

                    {section.id !== "SPORTS" &&
                      !state?.loading &&
                      currentGames.length === 0 &&
                      sIndex === activeIndex && (
                        <div className="hg-state hg-state--big">No games available</div>
                      )}

                    {section.id !== "SPORTS" &&
                      !state?.loading &&
                      currentGames.map((game, index) => (
                        <div
                          key={game?.id || game?.uid || game?.game_code || `${section.id}-${index}`}
                          ref={(el) => (tabRefs.current[index] = el)}
                          onClick={() =>
                            handleGameClick(
                              getGameCode(game),
                              getGamePlatform(game),
                              getGameType(game, section.apiType)
                            )
                          }
                          className="tab-item"
                        >
                          <span className="tab-icon">
                            <img
                              src={getCardImage(game, API)}
                              alt={getGameTitle(game)}
                              onError={(e) => {
                                e.currentTarget.src = `https://placehold.co/240x240/101233/ffffff?text=${encodeURIComponent(
                                  getGameTitle(game)
                                )}`;
                              }}
                            />
                          </span>
                          <span className="tab-name">{getGameTitle(game)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <LoginModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onLogin={(data) => console.log("login data:", data)}
          onRegister={() => console.log("register")}
          onForgotPassword={() => console.log("forgot password")}
        />
      </div>

      {showBlockedModal && null}
      {error && null}
    </>
  );
}